'use client'

import { useState } from 'react'

import { Link, useLocation } from 'react-router-dom'

import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from '@headlessui/react'

import {
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/20/solid'

const menu1 = [
  { name: 'Our History', href: '/history'},
  { name: 'Officers & Board', href: '/oli-family'},
  { name: 'Fallen Heroes', href: '/fallen-heroes'},
  { name: 'FAQs', href: '/faq'},
]

const menu2 = [
  { name: 'Meet, Greet & Grow', href: '/meet-greet-grow'},
  { name: 'Our Programs', href: '/programs'},
  { name: 'Join Patriotic Gardens', href: '/patriotic-signup'},
  { name: 'Gold Star Families', href: '/gold-star-families'},
  { name: 'Gold Star Gala', href: '/annual-gala'},
  { name: 'Home Depot Event', href: '/patriotic-gardens-event'},
]

const menu3 = [
  { name: 'All Blog Posts', href: '/blogs'},
  { name: 'Event Blog Posts', href: '/blogs?type=Event'},
  { name: 'Garden Blog Posts', href: '/blogs?type=Garden'},
  { name: 'Fundraiser Blog Posts', href: '/blogs?type=Fundraiser'},
]

const menu4 = [
  { name: 'Donate', href: '/donate'},
  { name: 'Find a Local Pantry', href: 'https://www.chicagosfoodbank.org/find-food-2/#find-food'},
]

const NAV_SECTIONS = [
  { key: 'about', label: 'About Us', items: menu1 },
  { key: 'events', label: 'Take Action', items: menu2 },
  { key: 'blog', label: 'OLI Blog', items: menu3 },
  { key: 'support', label: 'Support', items: menu4 },
]

const Menu = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openPopover, setOpenPopover] = useState(null)
  const [timeoutId, setTimeoutId] = useState(null)
  const location = useLocation()

  const handleMouseEnter = (popoverName) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      setTimeoutId(null)
    }
    setOpenPopover(popoverName)
  }

  const handleMouseLeave = () => {
    const id = setTimeout(() => {
      setOpenPopover(null)
    }, 100)
    setTimeoutId(id)
  }

  // A top-level section is "active" when the current route lives inside it.
  const sectionIsActive = (items) =>
    items.some((item) => !item.href.startsWith('http') && location.pathname === item.href.split('?')[0])

  return (
    <header className="sticky top-0 z-50">
      <nav
        aria-label="Global"
        className="flex w-full bg-forest text-cream py-3 px-4 md:px-8 items-center"
        style={{ boxShadow: 'var(--shadow-nav)' }}
      >
        {/* Logo — left */}
        <div className="flex items-center gap-x-3">
          <Link to="/" className="flex items-center gap-x-3">
            <span className="sr-only">OLI Gardens</span>
            <img
              alt="OLI Gardens logo"
              src="/official_logo.png"
              className="h-12 md:h-14 w-auto flex-shrink-0"
            />
            <span className="text-lg md:text-xl font-bold text-cream font-lulo whitespace-nowrap">
              OLI Gardens
            </span>
          </Link>
        </div>

        {/* Nav — center */}
        <PopoverGroup className="hidden lg:flex gap-x-10 flex-1 justify-center">
          {NAV_SECTIONS.map((section) => (
            <Popover key={section.key} className="relative">
              <PopoverButton
                className={`oli-navlink ${sectionIsActive(section.items) ? 'is-active' : ''} flex items-center gap-x-1 text-sm font-body font-bold uppercase tracking-label text-cream hover:text-gold_soft transition-colors duration-200 focus:outline-none`}
                onMouseEnter={() => handleMouseEnter(section.key)}
                onMouseLeave={handleMouseLeave}
              >
                {section.label}
                <ChevronDownIcon aria-hidden="true" className="size-4 flex-none text-gold_soft/70" />
              </PopoverButton>
              <PopoverPanel
                static={openPopover === section.key}
                className="absolute top-full left-1/2 z-50 mt-4 w-64 overflow-hidden rounded-card bg-forest_deep border border-gold/25 shadow-card-hover transition data-closed:translate-y-1 data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in transform -translate-x-1/2"
                onMouseEnter={() => handleMouseEnter(section.key)}
                onMouseLeave={handleMouseLeave}
              >
                <div className="p-2 flex flex-col">
                  {section.items.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className={`block rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors duration-150 hover:bg-forest_soft hover:text-gold_soft ${
                        location.pathname === item.href.split('?')[0] && !item.href.startsWith('http')
                          ? 'text-gold_soft'
                          : 'text-cream'
                      }`}
                      {...(item.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </PopoverPanel>
            </Popover>
          ))}
        </PopoverGroup>

        {/* Right: Donate CTA (desktop) + hamburger (mobile) */}
        <div className="flex items-center ml-auto gap-x-3">
          <a
            href="https://olipatrioticgardensshop.myspreadshop.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:inline-flex items-center justify-center px-6 py-2.5 rounded-btn border border-gold/60 text-gold_soft hover:bg-gold/10 font-body font-bold text-sm uppercase tracking-label transition-colors duration-200"
          >
            Merch Shop
          </a>
          <Link
            to="/donate"
            className="hidden lg:inline-flex items-center justify-center px-6 py-2.5 rounded-btn bg-flag_red hover:bg-flag_red_deep text-white font-body font-bold text-sm uppercase tracking-label transition-colors duration-200 oli-pulse"
          >
            Donate
          </Link>
          <div className="flex lg:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-cream">
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="size-8" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu — slides in from the right over a blurred backdrop */}
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-50 bg-forest_deep/40 backdrop-blur-sm" aria-hidden="true" />
        <DialogPanel className="oli-mobile-panel fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-forest p-6 sm:max-w-sm sm:ring-1 sm:ring-gold/20">
          <div className="flex items-center justify-between">
            <a href="/" className="-m-1.5 p-1.5">
              <span className="sr-only">OLI Gardens</span>
              <img
                alt="OLI Gardens logo"
                src="/official_logo.png"
                className="h-10 w-auto"
              />
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-cream">
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gold/15">
              <div className="space-y-2 py-6">
                {NAV_SECTIONS.map((section) => (
                  <Disclosure key={section.key} as="div" className="-mx-3">
                    <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2.5 pr-3.5 pl-3 text-base font-bold uppercase tracking-label text-cream hover:bg-forest_soft">
                      {section.label}
                      <ChevronDownIcon aria-hidden="true" className="size-5 flex-none text-gold_soft group-data-open:rotate-180" />
                    </DisclosureButton>
                    <DisclosurePanel className="mt-2 space-y-1">
                      {section.items.map((item) => (
                        <DisclosureButton
                          key={item.name}
                          as="a"
                          href={item.href}
                          className="block rounded-lg py-2 pr-3 pl-6 text-sm font-semibold text-cream/90 hover:bg-forest_soft hover:text-gold_soft"
                        >
                          {item.name}
                        </DisclosureButton>
                      ))}
                    </DisclosurePanel>
                  </Disclosure>
                ))}
              </div>
              <div className="py-6">
                <a
                  href="/donate"
                  className="flex w-full items-center justify-center px-6 py-3.5 rounded-btn bg-flag_red hover:bg-flag_red_deep text-white font-bold text-sm uppercase tracking-label transition-colors duration-200"
                >
                  Donate
                </a>
                <a
                  href="https://olipatrioticgardensshop.myspreadshop.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 flex w-full items-center justify-center px-6 py-3.5 rounded-btn border border-gold/60 text-gold_soft font-bold text-sm uppercase tracking-label transition-colors duration-200 hover:bg-gold/10"
                >
                  Merch Shop
                </a>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  )
}

export default Menu;
